import { useCallback } from 'react';
import { authService } from '../services/auth';
import { SessionStatus, SelectedUnit } from '../types/auth';
import { useAppDispatch, useAppSelector } from '../store';
import {
  checkAuth as checkAuthThunk,
  logoutThunk,
  setAuthenticated as setAuthenticatedAction,
} from '../store/authSlice';

export interface AuthHookResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionStatus: SessionStatus | null;
  selectedUnit: SelectedUnit | null;
  userName: string | null;
  permissions: string[];
  login: () => void;
  logout: () => Promise<void>;
  checkAuth: (force?: boolean) => Promise<void>;
  setAuthenticated: (value: boolean) => void;
}

export const useAuth = (): AuthHookResult => {
  const dispatch = useAppDispatch();

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isLoading = useAppSelector((state) => state.auth.isLoading);
  const sessionStatus = useAppSelector((state) => state.auth.sessionStatus);
  const selectedUnit = useAppSelector((state) => state.auth.selectedUnit);
  const userName = useAppSelector((state) => state.auth.userName);
  const permissions = useAppSelector((state) => state.auth.permissions);

  const login = useCallback(() => {
    authService.login();
  }, []);

  const logout = useCallback((): Promise<void> => {
    return dispatch(logoutThunk());
  }, [dispatch]);

  const checkAuth = useCallback(
    (force: boolean = false): Promise<void> => {
      return dispatch(checkAuthThunk(force));
    },
    [dispatch]
  );

  const setAuthenticated = useCallback(
    (value: boolean): void => {
      dispatch(setAuthenticatedAction(value));
    },
    [dispatch]
  );

  return {
    isAuthenticated,
    isLoading,
    sessionStatus,
    selectedUnit,
    userName,
    permissions,
    login,
    logout,
    checkAuth,
    setAuthenticated,
  };
};
